import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HelpWidget from "./components/HelpWidget";
import ContextualHelp from "./components/ContextualHelp";
import LandingPage from "./pages/LandingPage";
import RecordingStudio from "./pages/RecordingStudio";
import Dashboard from "./pages/Dashboard";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import { TermsPage, PrivacyPage, CookiePolicyPage, CompliancePage } from "./pages/LegalPages";
import CookieConsent from "./components/CookieConsent";
import VideoPlayback from "./pages/VideoPlayback";
import BoothMode from "./pages/BoothMode";
import RecordingGuide from "./pages/RecordingGuide";
import AdminDashboard from "./pages/AdminDashboard";
import AdminDocuments from "./pages/AdminDocuments";
import AdminTranslationManager from "./pages/AdminTranslationManager";
import BookBooth from "./pages/BookBooth";
import ProfileCreate from "./pages/ProfileCreate";
import ProfileEdit from "./pages/ProfileEdit";
import PublicProfile from "./pages/PublicProfile";
import PublicInvoice from "./pages/PublicInvoice";
import LeadershipProfileCreate from "./pages/LeadershipProfileCreate";
import LeadershipProfileEdit from "./pages/LeadershipProfileEdit";
import PublicLeadershipProfile from "./pages/PublicLeadershipProfile";
import TestimonialRecord from "./pages/TestimonialRecord";
import ConnectBoard from "./pages/ConnectBoard";
import PostOpportunity from "./pages/PostOpportunity";
import MyOpportunities from "./pages/MyOpportunities";
import ClaimProfile from "./pages/ClaimProfile";
import HelpPage from "./pages/HelpPage";
import VideoGuidelinesPage from "./pages/VideoGuidelinesPage";
import CampaignLanding from "./pages/CampaignLanding";
import AboutPage from "./pages/AboutPage";
import ShowcaseEventPage from "./pages/ShowcaseEventPage";
import PublicOpportunityPage from "./pages/PublicOpportunityPage";
import DiscoverPage from "./pages/DiscoverPage";
import PricingPage from "./pages/PricingPage";
import RenewPage from "./pages/RenewPage";
import { Toaster } from "./components/ui/sonner";
import FounderMessagePage from "./pages/FounderMessagePage";
import PodcastPage from "./pages/PodcastPage";
import MyConsentsPage from "./pages/MyConsentsPage";
import ConsentGate from "./components/ConsentGate";
import InvitationPage from "./pages/InvitationPage";
import { ReferralGuidePage, PartnerProgrammePage } from "./pages/ReferralGuidePages";
import BrandStageGuidePage from "./pages/BrandStageGuidePage";
import LegalCompletePage from "./pages/LegalCompletePage";
import "./index.css";

// Layout component that conditionally renders header/footer
function Layout({ children }) {
  const location = useLocation();
  const isVideoPlayback = location.pathname.startsWith('/v/');
  const isInvitationPage = location.pathname.startsWith('/invite/') || location.pathname.startsWith('/renew/');
  const isBoothMode = location.pathname.startsWith('/booth');
  const isRecordingGuide = location.pathname.startsWith('/recording-guide');
  const isAdmin = location.pathname.startsWith('/admin');
  const isBookBooth = location.pathname.startsWith('/book-booth');
  const isPublicProfile = location.pathname.startsWith('/p/');
  const isLeadershipProfile = location.pathname.startsWith('/l/') || location.pathname.startsWith('/leadership');
  const isFounderPage = location.pathname.startsWith('/message-from-founder') || location.pathname.startsWith('/podcast');
  const isReferralGuide = location.pathname.startsWith('/referral-programme') || location.pathname.startsWith('/partner-programme') || location.pathname === '/guide' || location.pathname === '/legal/complete';
  const isProfileCreate = location.pathname.startsWith('/profile');
  const isPublicInvoice = location.pathname.startsWith('/invoice/');
  const isTestimonial = location.pathname.startsWith('/testimonial');
  const isConnect = location.pathname.startsWith('/connect');
  const isClaimProfile = location.pathname.startsWith('/claim-profile');
  const isHelpPage = location.pathname.startsWith('/help');
  const isCampaignPage = location.pathname.startsWith('/go-global');
  const isDiscoverPage = location.pathname.startsWith('/discover');
  
  // Hide header/footer for video playback, booth mode, recording guide, booking, profiles, invoices, testimonials, connect, claim profile, help, campaign, discover, founder, and invitation pages
  const hideChrome = isVideoPlayback || isBoothMode || isRecordingGuide || isBookBooth || isPublicProfile || isProfileCreate || isPublicInvoice || isLeadershipProfile || isTestimonial || isConnect || isClaimProfile || isHelpPage || isCampaignPage || isDiscoverPage || isFounderPage || isInvitationPage || isReferralGuide;
  
  return (
    <>
      {!hideChrome && <Header />}
      <main>{children}</main>
      {!hideChrome && <Footer />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Toaster theme="dark" position="top-center" richColors />
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/discover" element={<DiscoverPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/renew/:token" element={<RenewPage />} />
            <Route path="/message-from-founder" element={<FounderMessagePage />} />
            <Route path="/podcast" element={<PodcastPage />} />
            <Route path="/invite/:invitationId" element={<InvitationPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/compliance" element={<CompliancePage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/studio" element={<RecordingStudio />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/v/:shareId" element={<VideoPlayback />} />
            <Route path="/booth" element={<BoothMode />} />
            <Route path="/recording-guide" element={<RecordingGuide />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/documents" element={<AdminDocuments />} />
            <Route path="/admin/translations" element={<AdminTranslationManager />} />
            <Route path="/book-booth" element={<BookBooth />} />
            <Route path="/profile/create" element={<ProfileCreate />} />
            <Route path="/profile/edit/:profileId" element={<ProfileEdit />} />
            <Route path="/p/:slug" element={<PublicProfile />} />
            <Route path="/p/:slug/showcase/:eventId" element={<ShowcaseEventPage />} />
            <Route path="/p/:slug/opportunity/:oppSlug" element={<PublicOpportunityPage />} />
            <Route path="/l/:slug/opportunity/:oppSlug" element={<PublicOpportunityPage />} />
            <Route path="/leadership/create" element={<LeadershipProfileCreate />} />
            <Route path="/leadership/edit/:profileId" element={<LeadershipProfileEdit />} />
            <Route path="/l/:slug" element={<PublicLeadershipProfile />} />
            <Route path="/leader/:slug" element={<PublicLeadershipProfile />} />
            <Route path="/invoice/:invoiceId/:token" element={<PublicInvoice />} />
            <Route path="/testimonial/:token" element={<TestimonialRecord />} />
            <Route path="/connect" element={<ConnectBoard />} />
            <Route path="/connect/post" element={<PostOpportunity />} />
            <Route path="/connect/my-opportunities" element={<MyOpportunities />} />
            <Route path="/connect/:oppId" element={<ConnectBoard />} />
            <Route path="/claim-profile/:token" element={<ClaimProfile />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/video-guidelines" element={<VideoGuidelinesPage />} />
            <Route path="/go-global" element={<CampaignLanding />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/account/privacy" element={<MyConsentsPage />} />
            <Route path="/referral-programme" element={<ReferralGuidePage />} />
            <Route path="/partner-programme" element={<PartnerProgrammePage />} />
            <Route path="/guide" element={<BrandStageGuidePage />} />
            <Route path="/legal/complete" element={<LegalCompletePage />} />
          </Routes>
        </Layout>
        {/* Contextual Help - page-specific tips */}
        <ContextualHelp />
        {/* Cookie Consent Banner */}
        <CookieConsent />
        <ConsentGate />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
